import {
  Box,
  Button,
  Columns,
  Container,
  Heading,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import { ExternalRoutes } from '@spaceship-fspl/helpers';
import { paddingX } from '@spaceship-fspl/styles';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { PanelSection } from 'components';
import * as React from 'react';
import styled from 'styled-components';

interface JoinPanelProps {
  title: string;
  tagline: string;
  steps: {
    stepIcon?: { url: string };
    step?: string | null | undefined;
  }[];
}

const JoinStepMobile: React.FC<
  React.PropsWithChildren<{ step: string; url: string }>
> = ({ step, url }) => (
  <Box display="flex" flexDirection="column" justifyContent="center">
    <Stack spaceY="sm" alignX="center">
      <img src={url} alt={`${step} icon`} />
      <Text variant={1} align="center">
        {step}
      </Text>
    </Stack>
  </Box>
);

const JoinStepTabletDesktop = styled.div<{ alignSelf: string }>`
  box-sizing: border-box;
  width: 20%;
  ${paddingX('xxs')}
  align-self: ${({ alignSelf }) =>
    alignSelf === 'bottom' ? 'flex-end' : 'flex-start'};
`;

export const JoinPanel: React.FC<React.PropsWithChildren<JoinPanelProps>> = ({
  steps,
  title,
  tagline,
}) => {
  return (
    <TrackingCategory category="Join Panel">
      <PanelSection backgroundColor="neutral.000">
        <Container>
          <Stack spaceY="lg" alignX="center">
            <div>
              <Stack spaceY={{ xs: 'lg', lg: 'xxl' }}>
                <Stack spaceY={{ xs: 'sm', lg: 'md' }}>
                  <Heading
                    variant={1}
                    color="neutral.100"
                    align="center"
                    component="h2"
                  >
                    {title}
                  </Heading>
                  <Text variant={1} color="neutral.085" align="center">
                    {tagline}
                  </Text>
                </Stack>
                <Visible
                  isHidden={{ xs: true, md: false }}
                  displayValue="block"
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    height={{ md: 256, lg: 200 }}
                  >
                    {steps.map(({ step, stepIcon }, index) => (
                      <JoinStepTabletDesktop
                        key={`super_join_${index}_${step}`}
                        alignSelf={(index + 1) % 2 === 0 ? 'bottom' : 'top'}
                      >
                        <Stack spaceY="xxs" alignX="center">
                          <img src={stepIcon?.url || ''} alt={`${step} icon`} />
                          <Text variant={1} align="center">
                            {step}
                          </Text>
                        </Stack>
                      </JoinStepTabletDesktop>
                    ))}
                  </Box>
                </Visible>
              </Stack>
              <Visible isHidden={{ xs: false, md: true }} displayValue="block">
                <Columns>
                  <Columns.Column width={2 / 4}>
                    <Stack spaceY="xl" alignX="center">
                      {steps
                        .filter((_, index) => (index + 1) % 2 !== 0)
                        .map(({ step, stepIcon }, index) => (
                          <JoinStepMobile
                            key={`${step}-${index}`}
                            step={step || ''}
                            url={stepIcon?.url || ''}
                          />
                        ))}
                    </Stack>
                  </Columns.Column>
                  <Columns.Column width={2 / 4}>
                    <Box marginTop="md">
                      <Stack spaceY="xl" alignX="center">
                        {steps
                          .filter((_, index) => (index + 1) % 2 === 0)
                          .map(({ step, stepIcon }, index) => (
                            <JoinStepMobile
                              key={`${step}-${index}`}
                              step={step || ''}
                              url={stepIcon?.url || ''}
                            />
                          ))}
                      </Stack>
                    </Box>
                  </Columns.Column>
                </Columns>
              </Visible>
            </div>
            <Button
              variant="primary"
              size="lg"
              href={ExternalRoutes.SUPER_SIGNUP}
            >
              Sign up
            </Button>
          </Stack>
        </Container>
      </PanelSection>
    </TrackingCategory>
  );
};
